import { useState } from 'react';
import { getCurrentHub } from '@sentry/nextjs';
import AdvertorialTag from '@/components/Internals/Header/AdvertorialTag';
import useBreakpoint from '@/hooks/useBreakpoint';
import { mglGray } from '@/styles/designSystemColors';
import classNames from '@/utils/className';
import Icon from '../Icon';
import PreferredSourceButton from '../PreferredSourceButton';

export type ShareButtonProps = {
  title: string;
  description: string;
  url: string;
  isAdvertorial?: boolean;
};

type ObjectButtonInternal = {
  name: string;
  link?: string;
  icon: 'whatsapp' | 'facebook' | 'link-variant' | 'email';
  bgColor: string;
  linkApp?: string;
  copyClipboard?: boolean;
  message?: string;
  show: boolean;
};

export default function InternalShareButton({ title, description, url, isAdvertorial = false }: ShareButtonProps) {
  const currentSpan = getCurrentHub().getScope()?.getSpan();
  const internalShareButtonSpan = currentSpan && currentSpan.startChild({ op: 'internalShareButton' });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile] = useBreakpoint('phone');

  const InternalBtnList: ObjectButtonInternal[] = [
    {
      name: 'Whatsapp',
      link: `https://web.whatsapp.com/send?text=${encodeURIComponent(title)} ${url}`,
      icon: 'whatsapp',
      bgColor: '#25d366',
      linkApp: `whatsapp://send?text=${encodeURIComponent(title)} ${url}`,
      show: isMenuOpen,
    },
    {
      name: 'Facebook',
      link: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      icon: 'facebook',
      bgColor: '#3b5998',
      show: isMenuOpen,
    },
    {
      name: 'E-mail',
      link: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description)}%0D%0A%0D%0A Veja o conteúdo completo em ${url}`,
      icon: 'email',
      bgColor: mglGray.mglGray900,
      show: isMenuOpen,
    },
    {
      name: 'Link',
      link: url,
      icon: 'link-variant',
      bgColor: '#999',
      message: 'Link copiado!',
      copyClipboard: true,
      show: isMenuOpen,
    },
  ];

  const MobileBtnList: ObjectButtonInternal[] = [
    {
      name: 'Whatsapp',
      link: `https://web.whatsapp.com/send?text=${encodeURIComponent(title)} ${url}`,
      icon: 'whatsapp',
      bgColor: '#25d366',
      linkApp: `whatsapp://send?text=${encodeURIComponent(title)} ${url}`,
      show: isMenuOpen,
    },
    {
      name: 'Facebook',
      link: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      icon: 'facebook',
      bgColor: '#3b5998',
      show: isMenuOpen,
    },
    {
      name: 'Link',
      link: url,
      icon: 'link-variant',
      bgColor: '#999',
      message: 'Link copiado!',
      copyClipboard: true,
      show: isMenuOpen,
    },
  ];

  const getCorrectLink = (btnProps: ObjectButtonInternal) => {
    let link: string;
    if (isMobile && (btnProps.name === 'Whatsapp' || btnProps.name === 'Telegram')) {
      link = btnProps.linkApp;
    } else if (btnProps.copyClipboard) {
      link = '';
    } else {
      link = btnProps.link;
    }
    return link;
  };

  // Mobile Layout Component
  const MobileLayout = () => (
    <div className="mb-8 flex flex-col gap-4">
      <div className="relative flex flex-col items-center">
        <div className="w-full">
          <div className="inline-flex w-full flex-row items-center justify-center gap-3 rounded-lg bg-mglGray50">
            <div className="flex w-full flex-row items-center gap-3">
              {url &&
                MobileBtnList.map(internalBtnProps => {
                  const Tag = internalBtnProps.name === 'Link' ? 'button' : 'a';
                  let linkProps = {};
                  if (getCorrectLink(internalBtnProps)) {
                    linkProps = {
                      href: getCorrectLink(internalBtnProps),
                      target: '_blank',
                      rel: 'noreferrer',
                    };
                  }

                  return (
                    <Tag
                      id={internalBtnProps.name}
                      className="share-button-click flex h-8 w-8 items-center justify-center rounded-full shadow-sm transition-all duration-200 hover:scale-105"
                      style={{ backgroundColor: internalBtnProps.bgColor, border: 0 }}
                      {...linkProps}
                      onClick={e => {
                        if (internalBtnProps.copyClipboard) {
                          e.preventDefault();
                          navigator.clipboard.writeText(internalBtnProps.link);
                          window.alert(internalBtnProps.message);
                        }
                        setIsMenuOpen(false);
                      }}
                      key={'btn-share-' + internalBtnProps.name}
                      title={`Compartilhar com ${internalBtnProps.name}`}
                    >
                      <div className={classNames('flex items-center justify-center text-xl', internalBtnProps.name === 'Link' && 'share-button-click')}>
                        <Icon
                          {...(internalBtnProps.name === 'Link' && { id: 'LinkSVG' })}
                          icon={`mdi:${internalBtnProps.icon}`}
                          width={20}
                          height={20}
                          color={mglGray.mglGray50}
                          viewBox={'0 0 24 24'}
                          {...(internalBtnProps.name === 'Link' && { className: 'share-button-click' })}
                        />
                      </div>
                    </Tag>
                  );
                })}
              <PreferredSourceButton />
            </div>
          </div>
        </div>
      </div>
      {isAdvertorial && <AdvertorialTag />}
    </div>
  );

  // Desktop Layout Component
  const DesktopLayout = () => (
    <div className="mb-8 flex flex-col flex-wrap justify-between gap-3 tablet:flex-row">
      <div className="relative flex flex-1 items-center">
        {url && <span className="mr-2 hidden text-base font-light text-mglGray700 tablet:inline desktop:hidden largeDesktop:inline">Compartilhe: </span>}
        <div className="inline-flex flex-1 flex-row items-center justify-center gap-2 rounded bg-mglGray50 transition-all duration-300 ease-ease">
          <div className="flex flex-1 flex-wrap justify-center gap-2 tablet:flex-nowrap tablet:justify-normal">
            {url &&
              InternalBtnList.map(internalBtnProps => {
                const Tag = internalBtnProps.name === 'Link' ? 'button' : 'a';
                let linkProps = {};
                if (getCorrectLink(internalBtnProps)) {
                  linkProps = {
                    href: getCorrectLink(internalBtnProps),
                    target: '_blank',
                    rel: 'noreferrer',
                  };
                }
                return (
                  <Tag
                    id={internalBtnProps.name}
                    className="share-button-click flex h-8 w-8 items-center justify-center rounded-half"
                    style={{ backgroundColor: internalBtnProps.bgColor, border: 0 }}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...linkProps}
                    onClick={e => {
                      if (internalBtnProps.copyClipboard) {
                        e.preventDefault();
                        navigator.clipboard.writeText(internalBtnProps.link);
                        window.alert(internalBtnProps.message);
                      }
                      setIsMenuOpen(false);
                    }}
                    key={'btn-share-' + internalBtnProps.name}
                    title={`Compartilhar com ${internalBtnProps.name}`}
                  >
                    <div className={classNames('flex items-center justify-center text-2xl', internalBtnProps.name === 'Link' && 'share-button-click')}>
                      <Icon
                        {...(internalBtnProps.name === 'Link' && { id: 'LinkSVG' })}
                        icon={`mdi:${internalBtnProps.icon}`}
                        width={18}
                        height={18}
                        color={mglGray.mglGray50}
                        viewBox={'0 0 24 24'}
                        {...(internalBtnProps.name === 'Link' && { className: 'share-button-click' })}
                      />
                    </div>
                  </Tag>
                );
              })}
            <PreferredSourceButton className="ml-auto" />
          </div>
        </div>
      </div>
      {isAdvertorial && <AdvertorialTag />}
    </div>
  );

  return (
    <>
      {isMobile ? <MobileLayout /> : <DesktopLayout />}

      {internalShareButtonSpan && internalShareButtonSpan.finish()}
    </>
  );
}
